import type Conversation from "@/models/conversation/Conversation";
import type ToneOfVoice from "@/models/tone-of-voice/toneOfVoice";

const truncate = (s: string, maxLen: number): string =>
  s.length <= maxLen ? s : `${s.slice(0, maxLen - 1)}…`;

export type ToneDetailsDisplay = {
  title: string;
  description: string;
  prompt: string;
};

/**
 * Label for the tone-of-voice chip: prefers snapshot title from the server, then
 * catalog title by id, then a short excerpt of the locked prompt string.
 */
export const getLockedToneChipLabel = (
  conversation: Conversation | undefined,
  toneCatalog: ToneOfVoice[] | undefined
): string => {
  if (!conversation || conversation.id === "temp-id") {
    return "";
  }

  const titleSnap = conversation.toneOfVoiceTitle?.trim();
  if (titleSnap) {
    return titleSnap;
  }

  if (conversation.toneOfVoiceId && toneCatalog?.length) {
    const match = toneCatalog.find((t) => t.id === conversation.toneOfVoiceId);
    if (match?.title?.trim()) {
      return match.title.trim();
    }
  }

  const prompt = conversation.toneOfVoice?.trim();
  if (prompt) {
    return truncate(prompt, 40);
  }

  return "";
};

/**
 * Full tone metadata for the detail dialog: prefers snapshots stored on the
 * conversation, then catalog lookup for older threads without snapshots.
 */
export const getToneDetailsForDisplay = (
  conversation: Conversation | undefined,
  toneCatalog: ToneOfVoice[] | undefined,
  pendingSelection: ToneOfVoice | undefined
): ToneDetailsDisplay => {
  if (!conversation || conversation.id === "temp-id") {
    return {
      title: pendingSelection?.title?.trim() ?? "",
      description: pendingSelection?.description?.trim() ?? "",
      prompt: pendingSelection?.promptString?.trim() ?? "",
    };
  }

  const fromCatalog =
    conversation.toneOfVoiceId && toneCatalog?.length
      ? toneCatalog.find((t) => t.id === conversation.toneOfVoiceId)
      : undefined;

  return {
    title:
      conversation.toneOfVoiceTitle?.trim() ||
      fromCatalog?.title?.trim() ||
      "",
    description:
      conversation.toneOfVoiceDescription?.trim() ||
      fromCatalog?.description?.trim() ||
      "",
    prompt:
      conversation.toneOfVoice?.trim() || fromCatalog?.promptString?.trim() || "",
  };
};
